import { create } from 'zustand';
import type { PlayerUser } from '../../entities/user/types';
import { readStorage, removeStorage, writeStorage } from '../../shared/lib/storage';
import { refreshSessionApi } from './api';
import { disableFirebasePush } from '../push/firebasePush';

const TOKEN_KEY = 'sorteia:authToken';
const EXPIRY_KEY = 'sorteia:authExpiresAt';
const SESSION_SECRET_KEY = 'sorteia:sessionSecret';
const GUEST_KEY = 'fd:guestMode';
const GUEST_STARTED_KEY = 'fd:guestStartedAt';
const GUEST_ID_KEY = 'fd:guestId';

/**
 * The device's stable guest id, minted on first use. Presence heartbeats key a
 * guest by it (`a:<id>` server-side), so it must be the same value every call
 * and the same one the room heartbeat sends.
 */
export function guestVisitorId(): string {
  const existing = readStorage(localStorage, GUEST_ID_KEY);
  if (existing) return existing;
  const minted = 'g_' + Math.random().toString(36).slice(2, 12);
  writeStorage(localStorage, GUEST_ID_KEY, minted);
  return minted;
}

function readToken() {
  return readStorage(sessionStorage, TOKEN_KEY) || readStorage(localStorage, TOKEN_KEY);
}

function readExpiry() {
  const raw = readStorage(sessionStorage, EXPIRY_KEY) || readStorage(localStorage, EXPIRY_KEY);
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function readSessionSecret() {
  return readStorage(sessionStorage, SESSION_SECRET_KEY) || readStorage(localStorage, SESSION_SECRET_KEY);
}

// In-flight refresh promise, shared so concurrent callers await one network round-trip.
let refreshInFlight: Promise<boolean> | null = null;

type AuthState = {
  token: string;
  expiresAt: number;
  sessionSecret: string;
  user: PlayerUser | null;
  guestMode: boolean;
  hydrated: boolean;
  setSession: (payload: {
    token: string;
    expiresAt?: number;
    sessionSecret?: string;
    user?: PlayerUser;
    remember?: boolean;
  }) => void;
  setUser: (user: PlayerUser | null) => void;
  enterGuest: () => void;
  clearGuest: () => void;
  logout: () => void;
  markHydrated: () => void;
  // Exchange the (possibly expired) access token + session secret for a fresh
  // access token, rotating the secret. Resolves true on success, false when the
  // session is gone (revoked / past the absolute cap) — caller should log out.
  refreshSession: () => Promise<boolean>;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  token: readToken(),
  expiresAt: readExpiry(),
  sessionSecret: readSessionSecret(),
  user: null,
  guestMode: readStorage(localStorage, GUEST_KEY) === '1',
  hydrated: false,

  setSession: ({ token, expiresAt = 0, sessionSecret, user, remember = true }) => {
    const nextSecret = sessionSecret ?? get().sessionSecret ?? '';
    writeStorage(sessionStorage, TOKEN_KEY, token);
    writeStorage(sessionStorage, EXPIRY_KEY, String(expiresAt || 0));
    if (nextSecret) writeStorage(sessionStorage, SESSION_SECRET_KEY, nextSecret);
    if (remember) {
      writeStorage(localStorage, TOKEN_KEY, token);
      writeStorage(localStorage, EXPIRY_KEY, String(expiresAt || 0));
      if (nextSecret) writeStorage(localStorage, SESSION_SECRET_KEY, nextSecret);
    } else {
      removeStorage(localStorage, TOKEN_KEY);
      removeStorage(localStorage, EXPIRY_KEY);
      removeStorage(localStorage, SESSION_SECRET_KEY);
    }
    removeStorage(localStorage, GUEST_KEY);
    removeStorage(localStorage, GUEST_STARTED_KEY);
    set({
      token,
      expiresAt,
      sessionSecret: nextSecret,
      user: user || get().user || null,
      guestMode: false,
      hydrated: true,
    });
  },

  refreshSession: () => {
    if (refreshInFlight) return refreshInFlight;
    const { token, sessionSecret, guestMode } = get();
    if (!token || !sessionSecret || guestMode) return Promise.resolve(false);
    refreshInFlight = (async () => {
      try {
        const data = await refreshSessionApi(token, sessionSecret);
        const remember = Boolean(readStorage(localStorage, TOKEN_KEY));
        get().setSession({
          token: data.token,
          expiresAt: Number(data.expires_at ?? data.expiresAt ?? 0),
          sessionSecret: data.sessionSecret,
          user: data.user,
          remember,
        });
        return true;
      } catch (err: unknown) {
        // 401/403 → session is truly gone; let the caller log out. Network blips
        // (offline, 5xx) shouldn't nuke the session — keep it and retry later.
        const status = (err as { status?: number } | null)?.status;
        if (status === 401 || status === 403) return false;
        return true;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  },

  setUser: (user) => set({ user, hydrated: true }),

  // Must drop the persisted session too, not just the tab-scoped one. readToken()
  // falls back to localStorage, so a "Keep me signed in" user who taps *Enter as
  // guest* gets silently logged back into their own account on the next full load —
  // with their balance and bet history on screen. On a shared phone "enter as guest"
  // is a privacy promise, so it has to clear exactly what logout() clears.
  enterGuest: () => {
    writeStorage(localStorage, GUEST_KEY, '1');
    writeStorage(localStorage, GUEST_STARTED_KEY, String(Math.floor(Date.now() / 1000)));
    if (!readStorage(localStorage, GUEST_ID_KEY)) {
      writeStorage(localStorage, GUEST_ID_KEY, 'g_' + Math.random().toString(36).slice(2, 12));
    }
    removeStorage(sessionStorage, TOKEN_KEY);
    removeStorage(sessionStorage, EXPIRY_KEY);
    removeStorage(sessionStorage, SESSION_SECRET_KEY);
    removeStorage(localStorage, TOKEN_KEY);
    removeStorage(localStorage, EXPIRY_KEY);
    removeStorage(localStorage, SESSION_SECRET_KEY);
    set({ token: '', expiresAt: 0, sessionSecret: '', user: null, guestMode: true, hydrated: true });
  },

  clearGuest: () => {
    removeStorage(localStorage, GUEST_KEY);
    removeStorage(localStorage, GUEST_STARTED_KEY);
    set({ guestMode: false });
  },

  logout: () => {
    const activeToken = get().token;
    if (activeToken) {
      // A shared browser must not keep receiving the previous player's pushes.
      // Firebase unregister happens best-effort before the local session is erased.
      void disableFirebasePush(activeToken).catch(() => undefined);
    }
    removeStorage(sessionStorage, TOKEN_KEY);
    removeStorage(sessionStorage, EXPIRY_KEY);
    removeStorage(sessionStorage, SESSION_SECRET_KEY);
    removeStorage(localStorage, TOKEN_KEY);
    removeStorage(localStorage, EXPIRY_KEY);
    // The signing secret was being left behind: readSessionSecret() falls back to
    // localStorage, so it outlived every logout on the device.
    removeStorage(localStorage, SESSION_SECRET_KEY);
    removeStorage(localStorage, GUEST_KEY);
    removeStorage(localStorage, GUEST_STARTED_KEY);
    set({ token: '', expiresAt: 0, sessionSecret: '', user: null, guestMode: false, hydrated: true });
  },

  markHydrated: () => set({ hydrated: true }),
}));
